import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function SupplierLoginPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // 통합관리자 계정 확인
      if (formData.email === 'wonderm' && formData.password === '*2025wonderm') {
        localStorage.setItem('supplier_auth', JSON.stringify({
          email: formData.email,
          role: 'admin',
          name: '통합관리자'
        }));
        navigate('/supplier');
        return;
      }

      // 일반 공급사 로그인 로직
      const { createClient } = await import('../../lib/supabase');
      const supabase = createClient();

      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (authError) throw authError;

      // 프로필 확인
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (profileError) throw profileError;

      if (profile.role !== 'supplier') {
        throw new Error('공급사 계정이 아닙니다.');
      }

      // 공급사 정보 확인
      const { data: supplier, error: supplierError } = await supabase
        .from('suppliers')
        .select('*')
        .eq('user_id', authData.user.id)
        .single();

      if (supplierError) throw supplierError;

      if (supplier.status !== 'approved') {
        throw new Error('승인 대기 중인 계정입니다. 관리자 승인 후 이용 가능합니다.');
      }

      localStorage.setItem('supplier_auth', JSON.stringify({
        email: formData.email,
        role: 'supplier',
        name: supplier.company_name,
        supplierId: supplier.id
      }));

      navigate('/supplier');
    } catch (err: any) {
      setError(err.message || '로그인에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('supplier_auth');
    setFormData({ email: '', password: '' });
    setError('');
    alert('로그아웃되었습니다.');
  };

  const isLoggedIn = localStorage.getItem('supplier_auth');

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-24">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-line text-4xl text-blue-600"></i>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">공급사 로그인</h1>
              <p className="text-gray-600">공급사 계정으로 로그인하세요</p>
            </div>

            {isLoggedIn ? (
              <div className="text-center">
                <div className="mb-6">
                  <i className="ri-checkbox-circle-line text-6xl text-green-600 mb-4"></i>
                  <p className="text-lg font-semibold text-gray-900 mb-2">로그인 상태입니다</p>
                  <p className="text-gray-600">{JSON.parse(isLoggedIn).name}</p>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/supplier')}
                    className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-dashboard-line mr-2"></i>
                    대시보드로 이동
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-logout-box-line mr-2"></i>
                    로그아웃
                  </button>
                </div>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      이메일
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <i className="ri-mail-line text-gray-400 text-lg"></i>
                      </div>
                      <input
                        type="text"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="off"
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                        placeholder="이메일을 입력하세요"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                      비밀번호
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <i className="ri-lock-line text-gray-400 text-lg"></i>
                      </div>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        autoComplete="new-password"
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                        placeholder="비밀번호를 입력하세요"
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isLoading ? '로그인 중...' : '로그인'}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    아직 계정이 없으신가요?{' '}
                    <Link to="/supplier/signup" className="text-blue-600 hover:text-blue-700 font-semibold cursor-pointer">
                      회원가입
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
